import React, { useEffect } from 'react';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { REmployees, Role } from '../../../../features/roles/types';
import { filterRoles } from '../../../../features/roles/filter';
import { Lx } from '../../../../i18n/consts';
import { addRole } from '../../../../features/roles';
import { getProfile } from '../../../../features/profile';
import { getPermissions } from '../../../../features/permissions';
import { getAllEmployees } from '../../../../features/employees/short';
import BaseForm from '../../../../components/shared/form-controls/base-form';
import { NAVIGATION_ROUTES } from '../../../../routes/NavigationRoute';
import { FormInput } from '../../../../components/shared/form-controls/form-input';
import { FormCheckbox } from '../../../../components/shared/form-controls/form-checkbox';
import FormAutocomplete from '../../../../components/shared/form-controls/form-autocomplete';
import FormAccordion from '../../../../components/shared/form-controls/form-accordion';
import { AutoCompleteItemValue } from '../../../../components/shared/form-controls/form-autocomplete/types';
import { AccordionDetailImpl, AccordionGroup, AccordionGroupImpl } from '../../../../components/shared/form-controls/form-accordion/types';
import { TextField } from '../text-field/text-field';
import { canManageRoleEmployees, canManageRolePermissions, canManageRoles } from '../../../../features/profile/entityPermissions';

interface CreateForm {
    name: string,
    isActive: boolean,
    useForProjectsNotifications: boolean,
    useAsVacationPrimaryApprover: boolean,
    permissions: number[],
    employees: REmployees[],
}

export default function RoleCreateForm() {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const navigate = useNavigate();
    const profile = useAppSelector(store => store.profile);
    const rolesState = useAppSelector(store => store.roles);
    const permissions = useAppSelector(store => store.permissions);
    const employees = useAppSelector(store => store.rEmployees);
    const [isLoadingEmployees, setIsLoadingEmployees] = React.useState(false);
    const canEditRole = canManageRoles(profile);
    const canEditRoleEmployees = rolesState.canShowAndEditEmployees || canManageRoleEmployees(profile);
    const canEditRolePermissions = rolesState.canShowAndEditPermissions || canManageRolePermissions(profile);

    const initialValue: CreateForm = {
        name: '',
        isActive: true,
        useForProjectsNotifications: false,
        useAsVacationPrimaryApprover: false,
        permissions: [],
        employees: [],
    };

    useEffect(() => {
        if (!canEditRole) {
            navigate(NAVIGATION_ROUTES.ROLES);
            return;
        }

        if (canEditRolePermissions && permissions.length === 0) {
            dispatch(getPermissions());
        }

    }, [canEditRole, canEditRolePermissions, dispatch, navigate, permissions.length]);

    const validateName = async (name: string): Promise<boolean> => {
        name = name.toLowerCase();
        if (name === '') {
            return Promise.resolve(false);
        }
        return await dispatch(filterRoles(name)).unwrap().then((values) => {
            return values.length < 1 || values.some(value => value.name.toLowerCase() !== name);
        });
    }

    const validationScheme: Yup.Schema<CreateForm> = Yup.object({
        name: Yup.string()
            .max(45, 'Must be 45 characters or less')
            .required(intl.formatMessage({ id: Lx.Form.NAME_REQUIRED_FORMAT }, { s: intl.formatMessage({ id: Lx.General.ROLE }) }))
            .test(
                'role-name-backend-validation',
                'Role name is taken',
                async (name: string) => { return await validateName(name);}
            ),
        isActive: Yup.boolean().required(),
        useForProjectsNotifications: Yup.boolean().required(),
        useAsVacationPrimaryApprover: Yup.boolean().required(),
        permissions: Yup.array().required().min(0),
        employees: Yup.array().of(Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required(),
            isDefault: Yup.boolean().required(),
        })).required().min(0),
    })

    const save = async (values: CreateForm) => {
        const updated: Role = {
            id: 0,
            name: values.name,
            isActive: values.isActive,
            useForProjectsNotifications: values.useForProjectsNotifications,
            useAsVacationPrimaryApprover: values.useAsVacationPrimaryApprover,
            employees: values.employees,
            permissions: values.permissions,
            canEditPermissions: canEditRolePermissions,
            canEditEmployees: canEditRoleEmployees,
        }

        await dispatch(addRole(updated)).unwrap();
        await dispatch(getProfile()).unwrap();
    }

    const ensureEmployeesLoaded = React.useCallback(() => {
        if (!canEditRoleEmployees || employees.length > 0 || isLoadingEmployees) {
            return;
        }

        setIsLoadingEmployees(true);
        dispatch(getAllEmployees())
            .finally(() => setIsLoadingEmployees(false));
    }, [canEditRoleEmployees, dispatch, employees.length, isLoadingEmployees]);

    if (!canEditRole) {
        return null;
    }

    const accordionGroups: AccordionGroup[] = permissions.map(group =>
        new AccordionGroupImpl(group.name,
            group.items.map(permission => new AccordionDetailImpl(permission.id, permission.name))
        ));
    
    return <Formik
        initialValues={initialValue}
        validationSchema={validationScheme}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={save}>
        <Form>
            <BaseForm contentGap={24} formType='create'
                formHeader='create_role'
                savingTitle='Role' 
                onClose={() => navigate(NAVIGATION_ROUTES.ROLES)} 
                onAfterSave={() => navigate(NAVIGATION_ROUTES.ROLES)}>
                <div style={{display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', maxWidth: '500px'}}>
                    <TextField text={intl.formatMessage({ id: Lx.Role.MAIN_INFORMATION })} />
                    <FormInput name='name' label='Name' limited={true} required={true} placeholder='Name' style={{width: '100%', maxWidth: '500px'}} />
                </div>
                {canEditRoleEmployees && (
                    <div style={{width: '100%', maxWidth: '500px'}}>
                        <FormAutocomplete
                            name='employees'
                            label='Employees'
                            placeHolder='Start typing to add'
                            items={employees as AutoCompleteItemValue[]}
                            hint='Leave empty to report on all'
                            onFocus={ensureEmployeesLoaded}
                        />
                    </div>
                )}
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', maxWidth: '500px'}}>
                    <FormCheckbox name='isActive' label='Active' style={{marginBottom: 0}} />
                    <FormCheckbox name='useForProjectsNotifications' label='Use for projects notifications' style={{marginBottom: 0}} />
                    <FormCheckbox name='useAsVacationPrimaryApprover' label='Use as vacation primary approver' style={{marginBottom: 0}} />
                </div>
                {canEditRolePermissions && (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', maxWidth: '500px', marginTop: '8px'}}>
                        <TextField text={intl.formatMessage({ id: Lx.Role.PERMISSIONS })} />
                        <FormAccordion name='permissions' groups={accordionGroups} elementType={'Switch'} />
                    </div>
                )}
            </BaseForm>
        </Form>
    </Formik>
}
